/**
 * SDR Stock Manager - Sorting & Product Grouping Utilities
 * @module utils/sorting
 */

import { safeParseDate } from './date.js';

/**
 * Determines character group priority for sorting:
 * 1: English / Latin alphabet (A-Z, a-z)
 * 2: Thai alphabet (ก-ฮ, Thai vowels/marks)
 * 3: Numbers (0-9)
 * 4: Other symbols
 * @param {string} str
 * @returns {number} 1 | 2 | 3 | 4
 */
export function getCharGroupPriority(str) {
  const trimmed = String(str || '').trim();
  if (!trimmed) return 4;
  const match = trimmed.match(/[a-zA-Z\u0E00-\u0E7F0-9]/);
  const char = match ? match[0] : trimmed.charAt(0);
  if (/^[a-zA-Z]/.test(char)) return 1; // English first
  if (/^[\u0E00-\u0E7F]/.test(char)) return 2; // Thai second
  if (/^[0-9]/.test(char)) return 3; // Numbers
  return 4; // Symbols / Others
}

/**
 * Compares two product names alphabetically:
 * English (A-Z) -> Thai (ก-ฮ) -> Numbers (0-9) -> Symbols
 * @param {string} nameA
 * @param {string} nameB
 * @returns {number}
 */
export function compareProductNameEnThenTh(nameA, nameB) {
  const cleanA = String(nameA || '').trim();
  const cleanB = String(nameB || '').trim();

  const priorityA = getCharGroupPriority(cleanA);
  const priorityB = getCharGroupPriority(cleanB);

  if (priorityA !== priorityB) {
    return priorityA - priorityB;
  }

  if (priorityA === 1) {
    return cleanA.localeCompare(cleanB, 'en', { sensitivity: 'base', numeric: true });
  }

  if (priorityA === 2) {
    return cleanA.localeCompare(cleanB, 'th', { sensitivity: 'base', numeric: true });
  }

  return cleanA.localeCompare(cleanB, undefined, { numeric: true });
}

/**
 * FEFO comparison comparator: soonest expiring first, items without expiry last.
 * When neither has expiry date, sorts by quantity descending.
 * @param {import('../types/index.js').InventoryItem} a
 * @param {import('../types/index.js').InventoryItem} b
 * @returns {number}
 */
export function compareLotsByExpiry(a, b) {
  const dateA = a && a.expiryDate ? safeParseDate(a.expiryDate) : null;
  const dateB = b && b.expiryDate ? safeParseDate(b.expiryDate) : null;
  const hasA = dateA && !isNaN(dateA.getTime());
  const hasB = dateB && !isNaN(dateB.getTime());

  if (hasA && hasB) {
    const diff = dateA.getTime() - dateB.getTime();
    if (diff !== 0) return diff;
  }
  if (hasA && !hasB) return -1;
  if (!hasA && hasB) return 1;

  // If both have no expiry (or identical expiry dates), sort by quantity descending
  return Number((b && b.quantity) || 0) - Number((a && a.quantity) || 0);
}

/**
 * Pure function to sort an array of inventory lots by FEFO.
 * @param {import('../types/index.js').InventoryItem[]} lots
 * @returns {import('../types/index.js').InventoryItem[]} New sorted array
 */
export function sortLotsByExpiry(lots) {
  if (!Array.isArray(lots)) return [];
  return [...lots].sort(compareLotsByExpiry);
}

/**
 * Generates the canonical composite product group key for an item.
 * @param {import('../types/index.js').InventoryItem|Object} item
 * @returns {string} `${name}||${brand}||${size}||${unit}` (lowercased)
 */
export function getProductGroupKey(item) {
  if (!item) return '';
  return `${String(item.name || '').trim().toLowerCase()}||${String(item.brand || '').trim().toLowerCase()}||${String(item.size || '').trim().toLowerCase()}||${String(item.unit || '').trim().toLowerCase()}`;
}

/**
 * Groups active (non-deleted) inventory items by product key into a Map of ProductGroup objects.
 * @param {import('../types/index.js').InventoryItem[]} items
 * @returns {Map<string, import('../types/index.js').ProductGroup>}
 */
export function groupInventoryByProduct(items) {
  const groups = new Map();
  if (!Array.isArray(items)) return groups;

  items.forEach(item => {
    if (!item || item.deleted) return;

    const key = getProductGroupKey(item);
    let group = groups.get(key);

    if (!group) {
      group = {
        key,
        name: String(item.name || '').trim(),
        brand: String(item.brand || '').trim(),
        size: String(item.size || '').trim(),
        unit: String(item.unit || '').trim(),
        image: item.image || '',
        notes: item.notes || '',
        lots: [],
        totalQuantity: 0,
        latestUpdatedAt: 0
      };
      groups.set(key, group);
    }

    // Accumulate total quantity
    group.totalQuantity = Number((group.totalQuantity + Number(item.quantity || 0)).toFixed(5));
    group.lots.push(item);

    // Prefer image from lots if first lot lacked image
    if (!group.image && item.image) {
      group.image = item.image;
    }
    if (!group.notes && item.notes) {
      group.notes = item.notes;
    }

    // Track latest modification timestamp
    const itemTime = item.updatedAt ? new Date(item.updatedAt).getTime() : 0;
    if (itemTime > group.latestUpdatedAt) {
      group.latestUpdatedAt = itemTime;
    }
  });

  // Sort lots within each group by FEFO
  groups.forEach(group => {
    group.lots.sort(compareLotsByExpiry);
  });

  return groups;
}

/**
 * Finds other active lots of the same product matching name, brand, size.
 * @param {import('../types/index.js').InventoryItem} item
 * @param {import('../types/index.js').InventoryItem[]} allItems
 * @returns {import('../types/index.js').InventoryItem[]}
 */
export function getOtherLots(item, allItems) {
  if (!item || !Array.isArray(allItems)) return [];
  const normalizedName = String(item.name || '').trim().toLowerCase();
  const normalizedBrand = String(item.brand || '').trim().toLowerCase();
  const normalizedSize = String(item.size || '').trim().toLowerCase();

  return allItems.filter(i =>
    i &&
    !i.deleted &&
    i.id !== item.id &&
    String(i.name || '').trim().toLowerCase() === normalizedName &&
    String(i.brand || '').trim().toLowerCase() === normalizedBrand &&
    String(i.size || '').trim().toLowerCase() === normalizedSize
  );
}

/**
 * Sorts an array of product groups alphabetically according to column and direction.
 * Supported columns: 'name' | 'brand' | 'size'.
 * Direction: 'asc' (A-Z / ก-ฮ) | 'desc' (Z-A / ฮ-ก).
 * 
 * Empty/blank values in 'brand' or 'size' are placed at the bottom.
 * Tiebreakers:
 * - When sorting by 'name': name -> brand -> size
 * - When sorting by 'brand': brand -> name -> size
 * - When sorting by 'size': size -> name -> brand
 *
 * @param {import('../types/index.js').ProductGroup[]} groups
 * @param {'name' | 'brand' | 'size' | string} [sortColumn='name']
 * @param {'asc' | 'desc'} [sortDirection='asc']
 * @returns {import('../types/index.js').ProductGroup[]} New sorted array
 */
export function sortProductGroups(groups, sortColumn = 'name', sortDirection = 'asc') {
  if (!Array.isArray(groups)) return [];
  const col = String(sortColumn || 'name').trim().toLowerCase();
  const dir = sortDirection === 'desc' ? 'desc' : 'asc';
  const multiplier = dir === 'desc' ? -1 : 1;

  return [...groups].sort((a, b) => {
    if (!a && !b) return 0;
    if (!a) return 1;
    if (!b) return -1;

    if (col === 'brand') {
      const brandA = String(a.brand || '').trim();
      const brandB = String(b.brand || '').trim();
      const hasA = Boolean(brandA && brandA !== '-');
      const hasB = Boolean(brandB && brandB !== '-');

      if (hasA && !hasB) return -1;
      if (!hasA && hasB) return 1;
      if (hasA && hasB) {
        const brandCmp = compareProductNameEnThenTh(brandA, brandB);
        if (brandCmp !== 0) return brandCmp * multiplier;
      }
      // Tiebreakers: name -> size
      const nameCmp = compareProductNameEnThenTh(a.name, b.name);
      if (nameCmp !== 0) return nameCmp;
      return compareProductNameEnThenTh(a.size, b.size);
    }

    if (col === 'size') {
      const sizeA = String(a.size || '').trim();
      const sizeB = String(b.size || '').trim();
      const hasA = Boolean(sizeA && sizeA !== '-');
      const hasB = Boolean(sizeB && sizeB !== '-');

      if (hasA && !hasB) return -1;
      if (!hasA && hasB) return 1;
      if (hasA && hasB) {
        const sizeCmp = compareProductNameEnThenTh(sizeA, sizeB);
        if (sizeCmp !== 0) return sizeCmp * multiplier;
      }
      // Tiebreakers: name -> brand
      const nameCmp = compareProductNameEnThenTh(a.name, b.name);
      if (nameCmp !== 0) return nameCmp;
      return compareProductNameEnThenTh(a.brand, b.brand);
    }

    // Default: sort by name
    const nameA = String(a.name || '').trim();
    const nameB = String(b.name || '').trim();
    const nameCmp = compareProductNameEnThenTh(nameA, nameB);
    if (nameCmp !== 0) return nameCmp * multiplier;

    // Tiebreakers: brand -> size
    const brandCmp = compareProductNameEnThenTh(a.brand, b.brand);
    if (brandCmp !== 0) return brandCmp;
    return compareProductNameEnThenTh(a.size, b.size);
  });
}

